import {
  Divider,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import { useHover } from '@mantine/hooks';
import type { IconFlag } from '@tabler/icons-react';
import type { ReactElement } from 'react';
import { ruleActions } from './constants';
import type { RuleAction } from './ruleSchemas';
import type { RuleTranslate } from './constants';
import { RuleMode } from '@billing/validation/rules';

export interface RuleActionConfig {
  value: RuleAction;
  label: string;
  description: string;
  color: string;
  Icon: typeof IconFlag;
  exampleText?: string;
}

export const getRuleActions = (translate: RuleTranslate): RuleActionConfig[] =>
  ruleActions.map((action) => ({
    ...action,
    label: translate(`pages:rules.builder.${action.labelKey}`),
    description: translate(`pages:rules.builder.${action.descriptionKey}`),
    exampleText: translate(`pages:rules.builder.${action.exampleKey}`),
  }));

export const getRuleAction = (
  value: RuleAction,
  translate: RuleTranslate
): RuleActionConfig | undefined =>
  getRuleActions(translate).find((action) => action.value === value);

export const getRuleActionOptions = (
  translate: RuleTranslate
): RuleActionConfig[] =>
  getRuleActions(translate).filter(
    (action) =>
      action.value !== 'flagOnly' && action.value !== RuleMode.TariffTransforms
  );

interface RuleActionCardProps {
  action: Pick<
    RuleActionConfig,
    'label' | 'description' | 'color' | 'Icon' | 'exampleText'
  >;
  value: string;
  selected: boolean;
  onSelect: () => void;
}

const RuleActionCard = ({
  action,
  value,
  selected,
  onSelect,
}: RuleActionCardProps): ReactElement => {
  const theme = useMantineTheme();
  const actionColors = theme.variantColorResolver({
    color: action.color,
    variant: 'light',
    theme,
  });
  const { hovered, ref } = useHover<HTMLButtonElement>();
  const icon = (
    <ThemeIcon size={34} radius="md" color={action.color} variant="light">
      <action.Icon size={20} />
    </ThemeIcon>
  );
  return (
    <Paper
      renderRoot={(props) => <UnstyledButton {...props} ref={ref} />}
      withBorder
      radius="lg"
      shadow={hovered ? 'xs' : undefined}
      bd={selected ? `1px solid ${action.color}.6` : undefined}
      p="md"
      bg={selected ? actionColors.background : 'transparent'}
      aria-pressed={selected}
      data-testid={`rule-mode-card-${value}`}
      onClick={onSelect}
      style={{
        alignSelf: 'stretch',
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        transform: hovered ? 'translateY(-2px)' : undefined,
      }}
    >
      <Group align="flex-start" wrap="nowrap">
        {icon}
        <Stack gap={4}>
          <Text fw={700} size="sm">
            {action.label}
          </Text>
          <Text c="dimmed" size="sm">
            {action.description}
          </Text>
          <Divider variant="solid" pb="xs" />
          <Text c="dimmed" size="xs">
            {action.exampleText}
          </Text>
        </Stack>
      </Group>
    </Paper>
  );
};

export const RuleActionPicker = ({
  value,
  onChange,
  translate,
}: {
  value: RuleAction;
  onChange: (value: RuleAction) => void;
  translate: RuleTranslate;
}): ReactElement => (
  <SimpleGrid cols={{ base: 1, sm: 3, md: 3 }} spacing="sm" display="flex">
    {getRuleActionOptions(translate).map((action) => (
      <RuleActionCard
        key={action.value}
        action={action}
        value={action.value}
        selected={value === action.value}
        onSelect={() => onChange(action.value)}
      />
    ))}
  </SimpleGrid>
);
